import { TableName } from '../components/TableFilter/types';
import { CurrentTypedInput } from '../containers/EventsPage/types';

export namespace TableTypes {
  export type TableRow = {
    key: string;
    title: string;
    unit?: string;
    classNames?: string;
  };

  export type GenericTableRow<T> = {
    key: keyof T;
    title: string;
    unit?: string;
    classNames?: string;
  };

  export type SortingObject = {
    key: string;
    direction: 'asc' | 'desc';
  };
}

export type Nullable<T> = T | null;

export type CaretDirection = 'forwards' | 'backwards';

export type CaretCustomEvent = CustomEvent<{
  direction: CaretDirection;
}>;

export type AwaitedApiResultType<T extends (...args: any) => any> = Awaited<
  ReturnType<T>
>;

export const FGG = 'FG grabs';
export const LoadingPerformance = 'Loading Performance';
export const DispenseWeight = 'Dispense weight';
export const DosingSpeed = 'Dosing speed';

// other common types
export const RESET_STORAGE = 'RESET_STORAGE';
export const RESET_STORAGE_WITHOUT_USER_SESSION =
  'RESET_STORAGE_WITHOUT_USER_SESSION';

export enum Robots {
  Vector = 'Vector robot',
  Astronaut = 'Astronaut robot',
}

export interface IGenericTable {
  searchValue?: string;
  prevSearchArr?: string[] | null;
  filters: Record<string, string[]>;
  sortingObject: TableTypes.SortingObject;
}

export type PerformanceIndicatorsStorage = {
  selectedKPI: string;
  expandedSectors: string[];
};

export type LocalStorageGlobalObject = {
  [K in AggregationTypes]: string;
} & {
  farmsListConfig: IGenericTable;
  favouriteFarmsListConfig: IGenericTable;
  astronautSettingsConfig: IGenericTable;
  amfSettingsConfig: Omit<IGenericTable, 'searchValue'>;
  fgGrabbsAggregation: {
    sortingObject: TableTypes.SortingObject;
  };
  feedHeightAggragation: {
    sortingObject: TableTypes.SortingObject;
  };
  loadResultTable: {
    sortingObject: TableTypes.SortingObject;
  };
  dispenseWeightAggregation: {
    sortingObject: TableTypes.SortingObject;
  };
  activityResultTable: {
    sortingObject: TableTypes.SortingObject;
  };
  backup: {
    sortingObject: TableTypes.SortingObject;
  };
  vectorConfig: {
    offEvents: string[];
  };
  adeConfig: {
    grids1: boolean;
    dots1: boolean;
    grids2: boolean;
    dots2: boolean;
    legend: boolean;
  };
  performanceIndicators: {
    selectedKPI: string;
  };
  updateHistory: {
    expandedDropdown: boolean;
  };
};

export type AggregationTypes = 'Height' | 'Dosing speed' | 'Accuracy';

export type LocalStorageAcceptableKeys = keyof LocalStorageGlobalObject;

export type FiltersByTableType = Record<TableName, string[]>;

export type LabelValuePairType = { label: string; value: string };

export type GetArrayType<T> = T extends (infer U)[] ? U : never;

export type MetricUnits =
  | 'g' // 3
  | 'g/s' // 3
  | 'cm' // 3
  | 'km' // 2
  | 'm' // 3
  | 'mm/s' // 3
  | 'mm' // 3
  | 'm' // 3
  | 'l' // 2
  | 'ml' // 3
  | 'kg' // depends on the weight > 1000 -> 2, < 1000 -> 300, > 9999 -> 0
  | 'kg/h' // depends on the weight > 1000 -> 2, < 1000 -> 300, > 9999 -> 0
  | 'kg/hr' // depends on the weight > 1000 -> 2, < 1000 -> 300, > 9999 -> 0
  | 'C' // 1
  | '°C' //  1
  | '%' // 2
  | 'hh:mm:ss'
  | 'kPa'
  | 'Pa'
  | 'A'
  | 'mA'
  | 's'
  | '';

export type ImperialUnits =
  | 'in' // 3
  | 'in/s' // 3
  | 'oz' // 3
  | 'oz/s' // 3
  | 'yd' // 3
  | 'mi' // 2
  | 'F' // 1
  | '°F' // 1
  | 'fl-oz' // 3
  | 'gal' // 3
  | 'lb' // depends on the weight > 1000 && < 9999 -> 2, > 9999 -> 0
  | 'lb/h' // depends on the weight > 1000 && < 9999 -> 2, > 9999 -> 0
  | 'lb/hr'; // depends on the weight > 1000 && < 9999 -> 2, > 9999 -> 0
export type Unit = MetricUnits | ImperialUnits;

export const EVENT_SEARCH_KEY_TYPES = [
  'name',
  'source',
  'id',
  'description',
  'device',
] as const;
export type ColumnType = (typeof EVENT_SEARCH_KEY_TYPES)[number];
export type EventsSearchObject =
  | Record<string, string>
  | CurrentTypedInput
  | null;
